const parseDimension = (value, fallback) => {
  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const parseCoordinate = (value, dimension) => {
  if (value?.endsWith('%')) {
    return (Number.parseFloat(value) / 100) * dimension;
  }

  return parseDimension(value, 0);
};

const parseTextTarget = (svgDocument, id, width, height, fallbackTarget) => {
  const element = svgDocument.getElementById(id);

  if (!element) {
    if (fallbackTarget) {
      return fallbackTarget;
    }

    throw new Error(`Certificate SVG is missing the #${id} text target.`);
  }

  return {
    x: parseCoordinate(element.getAttribute('x'), width),
    y: parseCoordinate(element.getAttribute('y'), height),
    dominantBaseline: element.getAttribute('dominant-baseline') || 'auto',
    textAnchor: element.getAttribute('text-anchor') || 'start',
    fontSize: parseDimension(element.getAttribute('font-size'), 16),
    fill: element.getAttribute('fill') || '#000000',
  };
};

const getLinkedImageSource = (svgDocument, rect) => {
  const patternId = rect
    .getAttribute('fill')
    ?.match(/^url\(#([^)]+)\)$/)?.[1];

  if (!patternId) {
    return null;
  }

  const patternUse = svgDocument
    .getElementById(patternId)
    ?.querySelector('use');
  const imageId =
    patternUse?.getAttribute('href') ||
    patternUse?.getAttribute('xlink:href');
  const image = imageId
    ? svgDocument.getElementById(imageId.replace(/^#/, ''))
    : null;

  return (
    image?.getAttribute('href') || image?.getAttribute('xlink:href') || null
  );
};

const parsePathElement = (path, index, defaultFill) => ({
  type: 'path',
  key: `certificate-path-${index}`,
  d: path.getAttribute('d'),
  fill: path.hasAttribute('fill')
    ? path.getAttribute('fill')
    : defaultFill,
  stroke: path.getAttribute('stroke') || undefined,
  strokeWidth: path.hasAttribute('stroke-width')
    ? parseDimension(path.getAttribute('stroke-width'), 1)
    : undefined,
  strokeMiterlimit: path.hasAttribute('stroke-miterlimit')
    ? parseDimension(path.getAttribute('stroke-miterlimit'), 4)
    : undefined,
});

export const parseCertificateTemplate = (svgMarkup, options = {}) => {
  const svgDocument = new DOMParser().parseFromString(
    svgMarkup,
    'image/svg+xml',
  );

  if (svgDocument.querySelector('parsererror')) {
    throw new Error('Unable to parse the certificate SVG.');
  }

  const svg = svgDocument.documentElement;
  const width = parseDimension(svg.getAttribute('width'), 792);
  const height = parseDimension(svg.getAttribute('height'), 612);
  const defaultFill = svg.getAttribute('fill') || undefined;
  const excludedPathIndexes = new Set(options.excludedPathIndexes || []);
  const textTargetIds = new Set([
    ...(options.includeDefaultTextTargets === false ? [] : ['name', 'date']),
    ...Object.keys(options.textTargets || {}),
  ]);
  let pathIndex = -1;
  const elements = Array.from(svg.querySelectorAll('path, rect'))
    .map((element, elementIndex) => {
      if (element.tagName.toLowerCase() === 'path') {
        pathIndex += 1;

        return excludedPathIndexes.has(pathIndex)
          ? null
          : parsePathElement(element, pathIndex, defaultFill);
      }

      const source = getLinkedImageSource(svgDocument, element);

      if (!source) {
        return null;
      }

      return {
        type: 'image',
        key: `certificate-image-${elementIndex}`,
        source,
        x: parseCoordinate(element.getAttribute('x'), width),
        y: parseCoordinate(element.getAttribute('y'), height),
        width: parseDimension(element.getAttribute('width'), 0),
        height: parseDimension(element.getAttribute('height'), 0),
        preserveAspectRatio: 'none',
      };
    })
    .filter(Boolean);

  return {
    width,
    height,
    viewBox: svg.getAttribute('viewBox') || `0 0 ${width} ${height}`,
    elements,
    paths: elements.filter((element) => element.type === 'path'),
    textTargets: Object.fromEntries(
      Array.from(textTargetIds).map((id) => [
        id,
        parseTextTarget(
          svgDocument,
          id,
          width,
          height,
          options.textTargets?.[id],
        ),
      ]),
    ),
  };
};

export const loadCertificateTemplate = async (templateUrl, options) => {
  const response = await fetch(templateUrl);

  if (!response.ok) {
    throw new Error(`Unable to load certificate SVG: ${response.status}`);
  }

  return parseCertificateTemplate(await response.text(), options);
};

export const getCertificateNameFontSize = (name, maximumFontSize = 36) => {
  return getCertificateTextFontSize(name, {
    maximumFontSize,
    minimumFontSize: 20,
    maxWidth: 560,
  });
};

export const getCertificateTextFontSize = (
  value,
  { maximumFontSize = 36, minimumFontSize = 10, maxWidth = 560 } = {},
) => {
  const estimatedFontSize =
    maxWidth / Math.max(String(value || '').length * 0.55, 1);

  return Math.max(
    minimumFontSize,
    Math.min(maximumFontSize, estimatedFontSize),
  );
};
